import {
  ISSUE_TITLE_MAX_LENGTH,
  type IssueSummary,
  type TodoTriageCard as TriageCard,
} from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { ORPCError } from '@orpc/client';
import { ExternalLink, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { formatIssueTime, ISSUE_VOICE_SOURCE_LABEL_KEYS } from '../../lib/issue-mgmt-format.js';
import { IssueSearchDialog } from './IssueSearchDialog.js';
import { IssueText } from './IssueText.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 原文を畳むまでの行数の目安（文字数で見る）。
 * @graph-connects none
 */
const RAW_FOLD_LENGTH = 240;

/**
 * 足すと判定待ちに戻る状態（保留・対応しない・解決済み）。
 * @graph-connects none
 */
const REOPENING: ReadonlySet<IssueSummary['displayStatus']> = new Set([
  'on_hold',
  'rejected',
  'resolved',
]);

/**
 * カードを済みにしたときに呼ぶ（結果の1行と、元に戻す操作）。conflict は他の人が先に振り分けていたとき。
 * @graph-connects none
 */
export interface CardOutcome {
  onDone: (label: string, operationId: string | null) => void;
  onConflict: () => void;
}

/**
 * やることの振り分けのカード（未振り分けの声の要点1件）。
 *
 * 「この声はどの課題？」と声の要点（AI下書きの札）・原文（伏せ字済み）・チャンネル・非公開の印・投稿日時・Slackで開くを出し、
 * 大ボタンは「新しい課題『（要点）』にする」。小さい選択肢は 課題ではない・ほかの課題を探す、「…」の中に あとで・声を直す。
 * どれも1クリックで保存して次のカードへ進み、トーストから元に戻せる。AI のおすすめ（足す先の候補）は別の PR で足す。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 当番が声の要点を1クリックで新しい課題・既存の課題・課題ではない・あとでに振り分け、声の要点を直すカード
 * @graph-connects useIssueMgmtWrite [calls] linkPoint・triagePoint・updatePointTitle
 * @graph-connects IssueSearchDialog [calls] ほかの課題を探す
 * @graph-connects useIssueToast [calls] 保存の知らせ
 */
export function TodoTriageCard({
  card,
  done,
  outcome,
  now,
}: {
  card: TriageCard;
  done: boolean;
  outcome: CardOutcome;
  now: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const linkPoint = useIssueMgmtWrite('linkPoint');
  const triage = useIssueMgmtWrite('triagePoint');
  const updateTitle = useIssueMgmtWrite('updatePointTitle');
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);
  const { point, voice } = card;
  const busy = linkPoint.isPending || triage.isPending || updateTitle.isPending;
  const expected = { expectedStatus: point.status, expectedIssueId: null };
  const newTitle = Array.from(point.title).slice(0, ISSUE_TITLE_MAX_LENGTH).join('');

  const failed = (error: unknown) => {
    toast.notifyError(error);
    if (error instanceof ORPCError && error.code === 'CONFLICT') outcome.onConflict();
  };

  const addToNew = () => {
    linkPoint.mutate(
      { pointId: point.pointId, newIssueTitle: newTitle, ...expected },
      {
        onSuccess: (result) => {
          const label = t('issueMgmt.triage.createdIssue', { title: newTitle });
          outcome.onDone(label, result.operationId);
          toast.notifySaved(label, {
            operationId: result.operationId,
            detail: t('issueMgmt.triage.createdIssueDetail'),
          });
        },
        onError: failed,
      },
    );
  };

  const addTo = (issue: IssueSummary) => {
    linkPoint.mutate(
      { pointId: point.pointId, issueId: issue.id, ...expected },
      {
        onSuccess: (result) => {
          setSearchOpen(false);
          const label = t('issueMgmt.triage.added', { title: issue.title });
          outcome.onDone(label, result.operationId);
          toast.notifySaved(label, {
            operationId: result.operationId,
            ...(REOPENING.has(issue.displayStatus)
              ? { detail: t('issueMgmt.triage.addedReopened', { title: issue.title }) }
              : {}),
          });
        },
        onError: (error) => {
          setSearchOpen(false);
          failed(error);
        },
      },
    );
  };

  const triagePoint = (action: 'not_issue' | 'later') => {
    setMenuOpen(false);
    triage.mutate(
      { pointId: point.pointId, action, ...expected },
      {
        onSuccess: (result) => {
          const label =
            action === 'not_issue' ? t('issueMgmt.triage.notIssued') : t('issueMgmt.triage.later');
          outcome.onDone(label, result.operationId);
          toast.notifySaved(label, {
            operationId: result.operationId,
            detail:
              action === 'not_issue'
                ? t('issueMgmt.triage.notIssuedDetail')
                : t('issueMgmt.triage.laterDetail'),
          });
        },
        onError: failed,
      },
    );
  };

  const saveTitle = () => {
    if (editing === null || editing.trim() === '') return;
    updateTitle.mutate(
      { pointId: point.pointId, base: point.title, value: editing.trim() },
      {
        onSuccess: (result) => {
          setEditing(null);
          toast.notifySaved(t('issueMgmt.triage.pointSaved'), {
            operationId: result.operationId,
            ...(result.maskedFields.length > 0 ? { detail: t('issueMgmt.form.masked') } : {}),
          });
        },
        onError: toast.notifyError,
      },
    );
  };

  const body = voice.body ?? '';
  const folded = !rawOpen && body.length > RAW_FOLD_LENGTH;
  const postedAt = formatIssueTime(voice.postedAt, voice.postedDate, now);

  return (
    <>
      <h2 className="px-5 pt-4 text-lg font-bold text-[var(--color-text-primary)]">
        {t('issueMgmt.triage.question')}
      </h2>
      <div className="space-y-3 px-5 py-4">
        <div>
          <p className="mb-1 flex items-center gap-2 text-xs font-semibold text-[var(--color-text-muted)]">
            {t('issueMgmt.triage.point')}
            {point.aiDraft && <Badge size="sm">{t('issueMgmt.content.aiDraft')}</Badge>}
          </p>
          {editing === null ? (
            <p className="text-base font-semibold text-[var(--color-text-primary)]">
              <IssueText text={point.title} />
            </p>
          ) : (
            <div className="space-y-2">
              <Input
                aria-label={t('issueMgmt.triage.point')}
                value={editing}
                maxLength={ISSUE_TITLE_MAX_LENGTH}
                onChange={(e) => {
                  setEditing(e.target.value);
                }}
                fullWidth
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={editing.trim() === ''}
                  loading={updateTitle.isPending}
                  onClick={saveTitle}
                >
                  {t('issueMgmt.common.save')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(null);
                  }}
                >
                  {t('issueMgmt.common.cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="rounded-lg border-l-4 border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm whitespace-pre-wrap text-[var(--color-text-secondary)]">
          {voice.bodyDeleted ? (
            <span className="text-[var(--color-text-muted)]">{t('issueMgmt.voices.deleted')}</span>
          ) : (
            <IssueText text={folded ? `${body.slice(0, RAW_FOLD_LENGTH)}…` : body} />
          )}
          {folded && (
            <button
              type="button"
              onClick={() => {
                setRawOpen(true);
              }}
              className="mt-1 block text-xs text-[var(--color-accent-primary)] hover:underline"
            >
              {t('issueMgmt.triage.openRaw')}
            </button>
          )}
        </div>
        <p className="flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-muted)]">
          {voice.channelName !== null && <span>#{voice.channelName}</span>}
          {voice.manualSource !== null && (
            <span>{t(ISSUE_VOICE_SOURCE_LABEL_KEYS[voice.manualSource])}</span>
          )}
          {voice.isPrivate && (
            <span title={t('issueMgmt.common.privateHint')}>
              <Badge size="sm">{t('issueMgmt.common.private')}</Badge>
            </span>
          )}
          {postedAt !== null && <span>{postedAt}</span>}
          {voice.slackUrl !== null && (
            <a
              href={voice.slackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[var(--color-accent-primary)] hover:underline"
            >
              {t('issueMgmt.common.openSlack')}
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
        </p>
      </div>
      {!done && (
        <div className="space-y-3 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-5 py-4">
          <Button
            size="lg"
            fullWidth
            loading={linkPoint.isPending}
            disabled={busy}
            onClick={addToNew}
          >
            {t('issueMgmt.triage.newIssue', { title: newTitle })}
          </Button>
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('issueMgmt.triage.noSuggestion')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                triagePoint('not_issue');
              }}
            >
              {t('issueMgmt.triage.notIssue')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setSearchOpen(true);
              }}
            >
              {t('issueMgmt.triage.searchIssue')}
            </Button>
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('issueMgmt.triage.more')}
                aria-expanded={menuOpen}
                disabled={busy}
                onClick={() => {
                  setMenuOpen((open) => !open);
                }}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute left-0 z-10 mt-1 w-32 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-1 shadow-lg"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      triagePoint('later');
                    }}
                    className="block w-full px-3 py-1.5 text-left text-sm hover:bg-[var(--color-bg-hover)]"
                  >
                    {t('issueMgmt.triage.laterAction')}
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setEditing(point.title);
                    }}
                    className="block w-full px-3 py-1.5 text-left text-sm hover:bg-[var(--color-bg-hover)]"
                  >
                    {t('issueMgmt.triage.editPoint')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {searchOpen && (
        <IssueSearchDialog
          pointTitle={point.title}
          pending={linkPoint.isPending}
          onClose={() => {
            setSearchOpen(false);
          }}
          onPick={addTo}
        />
      )}
    </>
  );
}
